'use client';
import React from 'react';
import GXGLayout from '@/components/GXGLayout';
import Link from 'next/link';

const CoinIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="#f59e0b"><circle cx="12" cy="12" r="10"/><text x="12" y="16" textAnchor="middle" fontSize="9" fill="#7c2d12" fontWeight="bold">G</text></svg>;
const CrownIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><path d="M2 20h20M4 20l2-10 6 5 6-5 2 10"/></svg>;
const ShieldIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const ControllerIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 12h4M8 10v4M15 11h.01M17 13h.01"/></svg>;
const StarIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const ArrowRightIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
const TrendUpIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;

const featuredGames = [
  { id: 1, title: 'Stellar Metroid', system: 'SNES', status: 'IN PROGRESS', statusColor: 'badge-green', mission: 'Defeat the Space Dragon', progress: 65, coins: 150, timeLeft: '2h 15m', gradient: 'from-purple-900 via-blue-900 to-indigo-900' },
  { id: 2, title: 'Chrono Quest', system: 'SNES', status: 'PLAY AGAIN', statusColor: 'badge-purple', mission: 'Complete Survival Mode', progress: 40, coins: 100, timeLeft: '1h 20m', gradient: 'from-orange-900 via-red-900 to-pink-900' },
  { id: 3, title: 'Retro Trigger', system: 'SNES', status: 'NEW QUEST', statusColor: 'badge-pink', mission: 'Reach the End of Time', progress: 20, coins: 200, timeLeft: '3h 45m', gradient: 'from-teal-900 via-cyan-900 to-blue-900' },
];

const leaderboard = [
  { rank: 1, name: 'NeoKai', tier: 'Legend', coins: 24680, color: 'text-gxg-gold' },
  { rank: 2, name: 'PixelRonin', tier: 'Platinum II', coins: 12450, color: 'text-gxg-purple2', isMe: true },
  { rank: 3, name: '8BitSamurai', tier: 'Platinum I', coins: 11870, color: 'text-gxg-blue2' },
  { rank: 4, name: 'RetroRyu', tier: 'Gold III', coins: 9540, color: 'text-white/70' },
  { rank: 5, name: 'ArcadeMaven', tier: 'Gold II', coins: 8320, color: 'text-white/70' },
];

const upcomingEvents = [
  { month: 'MAY', day: '24', name: 'Retro Night', location: 'GXG Lounge', time: '7:00 PM – 11:00 PM', badge: '2X COINS', badgeColor: 'badge-gold' },
  { month: 'JUN', day: '07', name: 'GXG Retro Tournament', location: 'Street Fighter II Turbo', time: '1:00 PM EST', badge: 'REGISTERED', badgeColor: 'badge-green' },
];

const recentActivity = [
  { player: 'PixelRonin', action: 'completed the mission "Boss Rush: Level 3"', coins: '+250', time: '2h ago' },
  { player: '8BitSamurai', action: 'earned the badge "Speed Runner"', coins: '+100', time: '4h ago' },
  { player: 'RetroRyu', action: 'completed "Collect 100 Coins"', coins: '+150', time: '6h ago' },
  { player: 'NeoKai', action: 'reached a new rank: Legend', coins: '+500', time: '8h ago' },
];

export default function HomePage() {
  return (
    <GXGLayout>
      <div className="max-w-[1400px] mx-auto">
        {/* Hero + Featured Quest */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Hero */}
          <div className="lg:col-span-2">
            <div className="glass-card p-8 relative overflow-hidden min-h-[220px]">
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-blue-900/10 pointer-events-none" />
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
              
              <div className="relative z-10">
                <h1 className="font-display font-black text-4xl md:text-5xl text-white leading-tight mb-2">
                  Build Your<br />
                  <span className="gxg-gradient-text">Player Legacy</span>
                </h1>
                <p className="text-white/60 text-sm mb-6 max-w-md">
                  Earn coins, unlock badges, and discover legendary retro games with the GXG community.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link href="/missions" className="btn-gxg-primary text-sm px-5 py-2.5 rounded-lg inline-flex items-center gap-2">
                    View Missions
                  </Link>
                  <Link href="/library" className="btn-gxg-outline text-sm px-5 py-2.5 rounded-lg inline-flex items-center gap-2">
                    Go to Library
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Quest */}
          <div className="glass-card p-5 relative overflow-hidden neon-border-pink">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-900/20 via-purple-900/10 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <div className="badge-gold text-[10px] mb-3 inline-flex items-center gap-1">
                <StarIcon /> TONIGHT&apos;S FEATURED QUEST
              </div>
              <div className="w-full h-32 rounded-lg bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 mb-3 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <span className="font-display font-black text-2xl text-gxg-pink opacity-80 relative z-10">GXG</span>
              </div>
              <h3 className="font-display font-bold text-lg text-white mb-1">Retro Night at GXG Lounge</h3>
              <p className="text-white/50 text-xs mb-4">Join the community, play classics, and earn 2X coins all night long!</p>
              <Link href="/events" className="btn-gxg-pink text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2 w-full justify-center">
                Join Quest <ArrowRightIcon />
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { icon: <CoinIcon />, label: 'COINS', value: '12,450', sub: '↑ 1,250 this week', subColor: 'text-gxg-green' },
            { icon: <CrownIcon />, label: 'RANK', value: 'Platinum II', sub: 'Top 12%', subColor: 'text-gxg-gold' },
            { icon: <ShieldIcon />, label: 'BADGES EARNED', value: '38', sub: 'Next: 42 / 50', subColor: 'text-gxg-blue2' },
            { icon: <ControllerIcon />, label: 'GAMES PLAYED', value: '127', sub: '↑ 12 this week', subColor: 'text-gxg-green' },
          ]?.map((stat, i) => (
            <div key={i} className="stat-card">
              <div className="flex items-center gap-2 mb-2">
                {stat?.icon}
                <span className="text-[10px] font-mono text-white/40 uppercase tracking-wider">{stat?.label}</span>
              </div>
              <div className="font-display font-bold text-xl text-white">{stat?.value}</div>
              <div className={`text-[11px] mt-1 flex items-center gap-1 ${stat?.subColor}`}>
                <TrendUpIcon />
                {stat?.sub}
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left: Journey + Activity */}
          <div className="xl:col-span-2 space-y-6">
            {/* Continue Your Journey */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg text-white">Continue Your Journey</h2>
                <Link href="/missions" className="text-gxg-purple2 text-xs hover:text-gxg-pink transition-colors">View All</Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {featuredGames?.map((game) => (
                  <div key={game?.id} className="game-card">
                    <div className={`h-36 bg-gradient-to-br ${game?.gradient} relative flex items-center justify-center`}>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <span className={`absolute top-2 left-2 ${game?.statusColor} text-[10px]`}>{game?.status}</span>
                      <span className="font-display font-black text-white text-sm relative z-10 text-center px-2">{game?.title}</span>
                    </div>
                    <div className="p-3">
                      <div className="text-xs text-white/50 mb-2">{game?.mission}</div>
                      <div className="progress-bar mb-2">
                        <div className="progress-fill-purple" style={{ width: `${game?.progress}%` }} />
                      </div>
                      <div className="flex justify-between text-[11px] text-white/40">
                        <span className="flex items-center gap-1"><CoinIcon /> {game?.coins}</span>
                        <span>{game?.timeLeft} left</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg text-white">Recent Activity</h2>
                <Link href="/passport" className="text-gxg-purple2 text-xs hover:text-gxg-pink transition-colors">View All</Link>
              </div>
              <div className="space-y-1">
                {recentActivity?.map((item, i) => (
                  <div key={i} className="leaderboard-row">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                      {item?.player?.slice(0, 2)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-white/90">{item?.player}</span>
                      <span className="text-sm text-white/50"> {item?.action}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-gxg-gold text-sm font-mono font-semibold">{item?.coins}</span>
                      <span className="text-white/30 text-xs">{item?.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Leaderboard + Events + Promo */}
          <div className="space-y-6">
            {/* Live Leaderboard */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-base text-white">Live Leaderboard</h2>
                <Link href="/missions" className="text-gxg-purple2 text-xs hover:text-gxg-pink transition-colors">View All</Link>
              </div>
              <div className="space-y-1">
                {leaderboard?.map((player) => (
                  <div key={player?.rank} className={`leaderboard-row ${player?.isMe ? 'bg-purple-500/10 rounded-lg' : ''}`}>
                    <span className={`w-6 text-center text-sm font-bold font-mono ${player?.rank === 1 ? 'text-gxg-gold' : 'text-white/40'}`}>{player?.rank}</span>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                      {player?.name?.slice(0, 2)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-semibold ${player?.color}`}>{player?.name}</div>
                      <div className="text-[10px] text-white/35">{player?.tier}</div>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-mono text-gxg-gold">
                      {player?.coins?.toLocaleString()} <CoinIcon />
                    </div>
                  </div>
                ))}
              </div>
              <Link href="/missions" className="btn-gxg-outline text-xs w-full mt-4 py-2 text-center block rounded-lg">
                See Full Leaderboard
              </Link>
            </div>

            {/* Upcoming Events */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-base text-white">Upcoming Events</h2>
                <Link href="/events" className="text-gxg-purple2 text-xs hover:text-gxg-pink transition-colors">View All</Link>
              </div>
              <div className="space-y-3">
                {upcomingEvents?.map((event, i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-lg bg-white/3 border border-white/5 hover:border-purple-500/20 transition-colors">
                    <div className="text-center flex-shrink-0 w-10">
                      <div className="text-[10px] font-mono text-white/40 uppercase">{event?.month}</div>
                      <div className="font-display font-bold text-xl text-white leading-none">{event?.day}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white/90">{event?.name}</div>
                      <div className="text-[11px] text-white/40">{event?.location}</div>
                      <div className="text-[11px] text-white/35">{event?.time}</div>
                    </div>
                    <span className={`${event?.badgeColor} self-start flex-shrink-0`}>{event?.badge}</span>
                  </div>
                ))}
              </div>
              <Link href="/events" className="btn-gxg-outline text-xs w-full mt-4 py-2 text-center block rounded-lg">
                Browse All Events
              </Link>
            </div>

            {/* GXG Promo */}
            <div className="glass-card p-5 neon-border-purple relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-blue-900/10 pointer-events-none" />
              <div className="relative z-10">
                <p className="text-white font-semibold text-base mb-1">The best retro games.</p>
                <p className="text-white font-semibold text-base mb-1">The best community.</p>
                <p className="text-lg font-bold mb-4">That&apos;s <span className="gxg-gradient-text font-display">GXG.</span></p>
                <button className="flex items-center gap-2 bg-[#5865F2] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#4752C4] transition-colors w-full justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
                  Join Discord
                </button>
                <div className="flex items-center gap-2 mt-2 text-xs text-white/40">
                  <span className="w-2 h-2 rounded-full bg-gxg-green animate-pulse" />
                  12,458 Members Online
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GXGLayout>
  );
}